import { useMemo } from "react";
import { FileRejection, FileWithPath, useDropzone } from "react-dropzone";

const baseStyle =
  "flex flex-col items-center px-4 py-8 border border-dashed rounded-md border-gray-300 bg-gray-50 text-gray-500 outline-none transition-colors duration-200";

const activeStyle = " border-secondary bg-secondary-light";

const acceptStyle = " border-primary bg-primary-light";

const rejectStyle = " border-red-500 bg-red-50";

const maxSize = 2 * 1024 * 1024 * 1024;

function fileSizeValidator(file: FileWithPath) {
  if (file.size > maxSize) {
    return {
      code: "size-too-large",
      message: `Datei ist größer als ${maxSize / (2 * 1024 * 1024 * 1024)} GB`,
    };
  }

  return null;
}

interface Props {
  uploadFiles: (files: FileWithPath[]) => void;
}

const MediaFileDropzone = ({ uploadFiles }: Props) => {
  const {
    getRootProps,
    getInputProps,
    open,
    fileRejections,
    acceptedFiles,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: ["image/*", "audio/*", "video/*", "application/pdf"],
    // Disable click and keydown behavior
    noClick: true,
    noKeyboard: true,
    validator: fileSizeValidator,
    multiple: false,
    onDrop: (files) => uploadFiles(files),
  });
  const className: string = useMemo(
    () => `${baseStyle}
      ${isDragActive && !(isDragReject || isDragAccept) ? activeStyle : ""}
      ${isDragAccept ? acceptStyle : ""}
      ${isDragReject ? rejectStyle : ""}`,
    [isDragActive, isDragReject, isDragAccept]
  );
  const acceptedFileItems = acceptedFiles.map((file: FileWithPath, index) => (
    <li key={file + "-index-" + index}>
      {file.path} - {file.size} bytes
    </li>
  ));

  const fileRejectionItems = fileRejections.map(
    ({ file, errors }: FileRejection & { file: FileWithPath }, index) => (
      <li key={file.size + "-index-" + index}>
        {file.path} - {file.size} bytes
        <ul>
          {errors.map((e) => (
            <li key={e.code}>{e.message}</li>
          ))}
        </ul>
      </li>
    )
  );

  return (
    <div className="container">
      <div {...getRootProps({ className })}>
        <input {...getInputProps()} />
        <p>
          Datei hier reinziehen, oder{" "}
          <a
            className="text-primary font-medium cursor-pointer hover:underline  "
            onClick={() => open()}
          >
            hier klicken.
          </a>
        </p>
        <p className="text-xs mt-2">Maximale Dateigröße: 2 GB</p>
      </div>
      <aside>
        <h4>Accepted files</h4>
        <ul>{acceptedFileItems}</ul>
        <h4>Rejected files</h4>
        <ul>{fileRejectionItems}</ul>
      </aside>
    </div>
  );
};

export default MediaFileDropzone;
